/**
 * CommercialSection (S1.4 → S2.2 row split) — ONE file, TWO mount points.
 *
 * S2.2 splits the old combined 商務與會計 section ROW-LEVEL per the xlsx
 * module routing (0708 col-A module, joined by label via the field registry):
 * - `CommercialSalesRows` → mounts under 銷售與定價 (DD!97–102 sales controls
 *   + the pricing-module basePrice/currency pair), flag-free — renders with
 *   `engines.pricing` OFF and lives OUTSIDE PricingSection.
 * - `CommercialAccountingRows` → mounts under 財務資料 (costingMethod + the
 *   R217 accounting placeholders; the registry-driven blocked→callout path is
 *   retained, though S8.1 unblocked DD!227–229 so no row is blocked today).
 *
 * The split is DATA (`COMMERCIAL_ROW_ROUTING` / `COMMERCIAL_ROW_MOUNTS`):
 * any future ruling moving a row between the two tabs is a one-line edit
 * here, enforced by `__tests__/tabComposition.test.tsx` (union == routed
 * registry rows, intersection empty, no row rendered by two tabs).
 *
 * Cost Estimate Type (DD!96, col-J Could) is NOT rendered yet: it has no
 * `Product`/registry home — it arrives with the Wave 3–5 pricing dataset
 * (S3.1 `CostEstimateType` union + S4.1 per-product assignments).
 *
 * R3 variant (RULING-3 2026-08-29, PROPOSED): the mounts accept a
 * `variant` PROP — 'engine' renames the accounting header and swaps its Edit
 * for a go-to-Sales-&-Pricing link (ONE edit seat); 'legacy' (default) is
 * byte-identical to today. The flag is read by index.tsx ONLY — this file
 * stays flag-token-free per the S2.2 mount contract (tabComposition pin).
 */

import { Button, Descriptions, Space, Tooltip, Typography, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import BlockedFieldCallout from '@/components/common/BlockedFieldCallout';
import { renderDash } from '@/components/common/TableContainer';
import { PRODUCT_FIELD_REGISTRY, getRulesBySection } from '@/config/productFieldRegistry';
import type { Product, ProductAccountingRefs } from '@/types/api/product';
import type { ProductDetailTabKey } from '../types';
import { visibleSalesRowKeys } from '../salesControlsVisibility';
import SectionHeader from './SectionHeader';

const { Text } = Typography;

export type CommercialRowGroup = 'sales' | 'accounting';

/* eslint-disable react-refresh/only-export-components -- the routing/mount
   DATA must live in the SAME module as the two row components it partitions
   (S2.2 one-file contract; asserted by __tests__/tabComposition.test.tsx). */

/**
 * Row-level module routing (S2.2) over the commercial registry rows this
 * section renders — registry-as-data: a re-routing ruling is a data edit.
 * The dims family (lengthMm/widthMm/heightMm/weightKg/weightUnit/cubicFeet)
 * renders in MainInfoSection under 基本資料 and is deliberately NOT routed
 * here (asserted in tabComposition.test.tsx to prevent double-mounting).
 */
export const COMMERCIAL_ROW_ROUTING = {
  basePrice: 'sales',
  currency: 'sales',
  itemDefinedCost: 'sales',
  itemDefinedCostUnit: 'sales',
  minQty: 'sales',
  minQtyUnit: 'sales',
  maxQty: 'sales',
  maxQtyUnit: 'sales',
  costingMethod: 'accounting',
} as const satisfies Record<string, CommercialRowGroup>;

export type CommercialRowKey = keyof typeof COMMERCIAL_ROW_ROUTING;

/**
 * Mount points: which detail tab each row group renders under. `accounting`
 * rides the `commercial` SECTION key (TAB_SECTION_GROUPS partition); `sales`
 * is mounted directly by `ProductDetailPage/index.tsx` from this data.
 */
export const COMMERCIAL_ROW_MOUNTS: Record<CommercialRowGroup, ProductDetailTabKey> = {
  sales: 'salesPricing',
  accounting: 'financialInfo',
};

export const commercialRowKeysByGroup = (group: CommercialRowGroup): CommercialRowKey[] =>
  (Object.keys(COMMERCIAL_ROW_ROUTING) as CommercialRowKey[]).filter(
    (key) => COMMERCIAL_ROW_ROUTING[key] === group
  );

/* eslint-enable react-refresh/only-export-components */

/** Registry is the single label edit point — join by key, render its labelKey. */
const labelKeyFor = (key: CommercialRowKey): string =>
  PRODUCT_FIELD_REGISTRY.find((rule) => rule.key === key)?.labelKey ?? key;

/**
 * P3 (2026-08-31) — read-back rows that need a DISCLOSURE beside their label.
 *
 * `basePrice` is the ONE row here whose number can contradict the Pricing
 * section beside it: it is the STORED master-data figure, while the Pricing
 * section's List Price chip is the engine's live `anchorListPrice`. Since the
 * Option-B flip they are computed from different things and routinely differ.
 * The Edit Commercial modal already discloses this (`fieldInfo.basePrice`);
 * the READ-BACK grid did not, so the contradiction was only explained on a
 * screen the reader has to open.
 *
 * ⚠ NOT FIXED HERE, and deliberately: in zh-TW this row's label and the
 * Pricing chip's label are BOTH 標準售價, so the two numbers collide under one
 * word on one tab. Renaming either is an ungated business-facing copy change
 * (the critic C-A2 precedent) and belongs to Melissa + 林芊/Judy, not to this
 * slice. The disclosure is the mitigation available without a rename.
 *
 * A static record, not an interpolated key: `audit-translations.cjs` cannot
 * resolve an interpolated key and reports one it cannot resolve as invalid.
 */
const ROW_INFO_KEY: Partial<Record<CommercialRowKey, string>> = {
  basePrice: 'products:detail.fieldInfo.basePriceReadback',
};

/** R3 (RULING-3): copy/entry variant — arrives as a PROP from index.tsx. */
export type CommercialSectionVariant = 'legacy' | 'engine';

interface CommercialRowsProps {
  product: Product;
  onEdit: () => void;
  variant?: CommercialSectionVariant;
}

/** Shared registry-driven value grid for one row group (no price arithmetic). */
const CommercialRowItems = ({ group, product }: { group: CommercialRowGroup; product: Product }) => {
  const { t } = useTranslation(['products']);
  const { token } = theme.useToken();

  const money = (value: number | null | undefined): string | undefined =>
    value != null ? `${product.currency ?? ''} ${value.toFixed(2)}`.trim() : undefined;

  const rowValue = (key: CommercialRowKey): React.ReactNode => {
    switch (key) {
      case 'basePrice':
        return renderDash(money(product.basePrice));
      case 'currency':
        return renderDash(product.currency);
      case 'itemDefinedCost':
        return renderDash(money(product.itemDefinedCost));
      case 'itemDefinedCostUnit':
        return renderDash(product.itemDefinedCostUnit);
      case 'minQty':
        return renderDash(product.minQty);
      case 'minQtyUnit':
        return renderDash(product.minQtyUnit);
      case 'maxQty':
        return renderDash(product.maxQty);
      case 'maxQtyUnit':
        return renderDash(product.maxQtyUnit);
      case 'costingMethod':
        return product.costingMethod
          ? t(`products:wizard.costingMethods.${product.costingMethod}`)
          : renderDash(undefined);
    }
  };

  const keys =
    group === 'sales' ? visibleSalesRowKeys(product) : commercialRowKeysByGroup(group);

  return (
    <Descriptions column={{ xs: 1, md: 3 }} layout="vertical" bordered>
      {keys.map((key) => {
        const infoKey = ROW_INFO_KEY[key];
        return (
          <Descriptions.Item
            key={key}
            label={
              infoKey ? (
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  {t(labelKeyFor(key))}
                  <Tooltip trigger={['hover', 'focus']} title={t(infoKey)}>
                    <InfoCircleOutlined
                      tabIndex={0}
                      aria-label={t('products:detail.infoAria')}
                      style={{ color: token.colorTextTertiary, fontSize: token.sizeXS }}
                    />
                  </Tooltip>
                </span>
              ) : (
                t(labelKeyFor(key))
              )
            }
          >
            {rowValue(key)}
          </Descriptions.Item>
        );
      })}
    </Descriptions>
  );
};

/**
 * 銷售與定價 mount — sales-control rows (DD!97–102 + basePrice/currency).
 * Renders flag-free (no FeatureGate) so the tab has content with
 * `engines.pricing` off; PricingSection mounts beside it in Wave 5.
 */
// R3: `variant` is accepted for signature symmetry — the sales mount keeps
// the ONE purple Edit and the Sales Controls title in BOTH variants.
export const CommercialSalesRows = ({ product, onEdit }: CommercialRowsProps) => {
  const { t } = useTranslation(['products', 'common']);

  return (
    <div>
      <SectionHeader
        title={t('products:detail.sections.commercialSales')}
        editLabel={t('common:actions.edit')}
        onEdit={onEdit}
        editDisabled={!product.isActive}
      />
      <CommercialRowItems group="sales" product={product} />
    </div>
  );
};

/**
 * 財務資料 mount — costingMethod + the accounting placeholders (R217),
 * registry-driven: any `blocked` row renders as a purple BlockedFieldCallout
 * (non-editable, no guess), the rest as read-only placeholder values pending
 * GL wiring. S8.1 lifted `blocked` from DD!227–229 (Sterling-rider trio, now
 * ordinary could rows) — the callout grid renders only when blocked rows
 * exist (hide-don't-disable, no empty container).
 */
export const CommercialAccountingRows = ({
  product,
  onEdit,
  variant = 'legacy',
  onGoToSalesPricing,
}: CommercialRowsProps & { onGoToSalesPricing?: () => void }) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();

  const accountingRules = getRulesBySection('accounting');
  const blockedRules = accountingRules.filter((rule) => rule.blocked);
  const placeholderRules = accountingRules.filter((rule) => !rule.blocked);

  const accountingValue = (ruleKey: string): string | null | undefined => {
    const key = ruleKey.replace('accounting.', '') as keyof ProductAccountingRefs;
    return product.accounting?.[key];
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        {variant === 'engine' ? (
          /* R3 ONE-edit-entry ruling: the financial mount loses its Edit —
             a navigation LINK to the one write seat replaces it (S3.2 link
             idiom). Navigation, not a write: it stays enabled regardless of
             isActive (hide-don't-disable governs write affordances; the Edit
             inside the destination still honors it). */
          <SectionHeader
            title={t('products:detail.sections.costingAccounting')}
            extra={
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={onGoToSalesPricing}
              >
                {t('products:detail.sections.editInSalesPricing')}
              </Button>
            }
          />
        ) : (
          <SectionHeader
            title={t('products:detail.sections.commercial')}
            editLabel={t('common:actions.edit')}
            onEdit={onEdit}
            editDisabled={!product.isActive}
          />
        )}
        <CommercialRowItems group="accounting" product={product} />
      </div>

      <div>
        <Typography.Title level={5}>{t('products:wizard.sections.accounting')}</Typography.Title>
        <Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: token.marginSM }}
        >
          {t('products:wizard.sections.accountingHint')}
        </Text>
        <Descriptions column={{ xs: 1, md: 3 }} layout="vertical" bordered size="small">
          {placeholderRules.map((rule) => (
            <Descriptions.Item key={rule.key} label={rule.labelKey ? t(rule.labelKey) : rule.key}>
              {renderDash(accountingValue(rule.key))}
            </Descriptions.Item>
          ))}
        </Descriptions>
        {blockedRules.length > 0 && (
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mt-4">
            {blockedRules.map((rule) => (
              <BlockedFieldCallout
                key={rule.key}
                label={rule.labelKey ? t(rule.labelKey) : rule.key}
                statusText={t('products:wizard.blocked.status')}
                description={t('products:wizard.blocked.help')}
              />
            ))}
          </div>
        )}
      </div>
    </Space>
  );
};
